import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MousePointerClick, ShoppingCart, IndianRupee, Clock, CheckCircle2, Copy, ExternalLink,
  LogOut, User2, TrendingUp, Wallet, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { API_BASE as API } from "@/config";
import axios from "axios";

const fadeIn = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

function StatCard({ icon, label, value, sub, color }) {
  return (
    <motion.div variants={fadeIn}>
      <Card className="bg-gray-900/60 border-gray-800 rounded-2xl" data-testid={`inf-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <CardContent className="p-4">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${color}`}>{icon}</div>
          <p className="text-xl font-bold text-white tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>{value}</p>
          <p className="text-gray-500 text-xs mt-0.5">{label}</p>
          {sub && <p className="text-gray-600 text-[10px] mt-0.5">{sub}</p>}
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function InfluencerDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const token = localStorage.getItem("inf_token");
  const infData = JSON.parse(localStorage.getItem("inf_data") || "{}");

  const fetchDashboard = useCallback(async () => {
    if (!token) { navigate("/influencer/login"); return; }
    try {
      const res = await axios.get(`${API}/influencer/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("inf_token");
        localStorage.removeItem("inf_data");
        navigate("/influencer/login");
      }
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleLogout = () => {
    localStorage.removeItem("inf_token");
    localStorage.removeItem("inf_data");
    navigate("/influencer/login");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { influencer, stats, recent_commissions, payouts } = data;
  const referralLink = `${window.location.origin}/?ref=${influencer.referral_code}`;
  const isPending = influencer.status === "pending";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 pb-8" data-testid="inf-dashboard">
      {/* Header */}
      <div className="bg-gray-900/80 border-b border-gray-800 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-sm">
              {influencer.name?.[0]?.toUpperCase() || "I"}
            </div>
            <div>
              <h1 className="text-white font-bold text-sm" style={{ fontFamily: 'Space Grotesk' }}>{influencer.name}</h1>
              <p className="text-gray-500 text-xs">{influencer.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
              influencer.status === "approved" ? "bg-green-500/10 text-green-400 border border-green-500/20" :
              influencer.status === "pending" ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20" :
              "bg-red-500/10 text-red-400 border border-red-500/20"
            }`} data-testid="inf-status-badge">{influencer.status}</span>
            <button onClick={handleLogout} className="p-2 text-gray-500 hover:text-red-400 transition-colors" data-testid="inf-logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 mt-6 space-y-6">
        {/* Pending notice */}
        {isPending && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-4 flex items-start gap-3" data-testid="inf-pending-notice">
            <AlertCircle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-300 text-sm font-semibold">Account Pending Approval</p>
              <p className="text-yellow-400/60 text-xs mt-1">Your application is under review. You&apos;ll be able to earn commissions once approved by the admin.</p>
            </div>
          </motion.div>
        )}

        {/* Referral Link */}
        <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-4" data-testid="inf-referral-section">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Your Referral Code</p>
          <div className="flex items-center gap-2 mb-3">
            <code className="bg-gray-800 text-amber-400 px-4 py-2.5 rounded-xl text-lg font-bold tracking-widest flex-1 text-center" style={{ fontFamily: 'Space Grotesk' }} data-testid="inf-ref-code">
              {influencer.referral_code}
            </code>
            <button onClick={() => copyToClipboard(influencer.referral_code)} className="p-2.5 bg-gray-800 rounded-xl text-gray-400 hover:text-amber-400 transition-colors" data-testid="inf-copy-code">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Referral Link</p>
          <div className="flex items-center gap-2">
            <div className="bg-gray-800 rounded-xl px-3 py-2.5 text-xs text-gray-300 flex-1 truncate font-mono" data-testid="inf-ref-link">{referralLink}</div>
            <button onClick={() => copyToClipboard(referralLink)} className="p-2.5 bg-gray-800 rounded-xl text-gray-400 hover:text-amber-400 transition-colors" data-testid="inf-copy-link">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <motion.div variants={stagger} initial="hidden" animate="visible" className="grid grid-cols-2 gap-3">
          <StatCard icon={<MousePointerClick className="w-4 h-4 text-blue-400" />} label="Total Clicks" value={stats.total_clicks} color="bg-blue-500/10" />
          <StatCard icon={<ShoppingCart className="w-4 h-4 text-green-400" />} label="Paid Orders" value={stats.total_orders} color="bg-green-500/10" />
          <StatCard icon={<IndianRupee className="w-4 h-4 text-amber-400" />} label="Total Sales" value={`₹${(stats.total_sales / 100).toFixed(0)}`} color="bg-amber-500/10" />
          <StatCard icon={<TrendingUp className="w-4 h-4 text-purple-400" />} label="Total Commission" value={`₹${(stats.total_commission / 100).toFixed(0)}`} color="bg-purple-500/10" />
          <StatCard icon={<Clock className="w-4 h-4 text-orange-400" />} label="Pending Commission" value={`₹${(stats.pending_commission / 100).toFixed(0)}`} sub={`${stats.pending_count} pending`} color="bg-orange-500/10" />
          <StatCard icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />} label="Paid Commission" value={`₹${(stats.paid_commission / 100).toFixed(0)}`} color="bg-emerald-500/10" />
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 bg-gray-900/60 rounded-xl p-1 border border-gray-800">
          {["overview", "payouts"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${tab === t ? "bg-amber-500/20 text-amber-400" : "text-gray-500 hover:text-gray-300"}`}
              data-testid={`inf-tab-${t}`}>
              {t === "overview" ? "Commission History" : "Payout History"}
            </button>
          ))}
        </div>

        {/* Commission History */}
        {tab === "overview" && (
          <div className="space-y-2">
            {recent_commissions.length === 0 ? (
              <p className="text-gray-600 text-sm text-center py-8">No commissions yet. Share your referral link to start earning!</p>
            ) : (
              recent_commissions.map((c, i) => (
                <motion.div key={c.commission_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="bg-gray-900/60 border border-gray-800 rounded-xl p-3 flex items-center justify-between" data-testid={`inf-commission-${i}`}>
                  <div>
                    <p className="text-white text-sm font-semibold">₹{(c.commission_amount / 100).toFixed(0)} commission</p>
                    <p className="text-gray-500 text-xs">Order ₹{(c.order_amount / 100).toFixed(0)} · {c.commission_type === "percentage" ? `${c.commission_rate}%` : `₹${(c.commission_rate / 100).toFixed(0)} fixed`}</p>
                    <p className="text-gray-600 text-[10px] mt-0.5">{new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                    c.status === "paid" ? "bg-green-500/10 text-green-400" :
                    c.status === "pending" ? "bg-yellow-500/10 text-yellow-400" :
                    "bg-red-500/10 text-red-400"
                  }`}>{c.status}</span>
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* Payout History */}
        {tab === "payouts" && (
          <div className="space-y-2">
            {payouts.length === 0 ? (
              <p className="text-gray-600 text-sm text-center py-8">No payouts yet.</p>
            ) : (
              payouts.map((p, i) => (
                <motion.div key={p.payout_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="bg-gray-900/60 border border-gray-800 rounded-xl p-3" data-testid={`inf-payout-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-white text-sm font-semibold">₹{(p.amount / 100).toFixed(0)}</p>
                    <span className="text-green-400 text-[10px] font-bold bg-green-500/10 px-2 py-0.5 rounded-full uppercase">{p.method}</span>
                  </div>
                  {p.reference && <p className="text-gray-400 text-xs mt-1">Ref: {p.reference}</p>}
                  {p.notes && <p className="text-gray-500 text-xs mt-0.5">{p.notes}</p>}
                  <p className="text-gray-600 text-[10px] mt-1">{new Date(p.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
